import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CountryPhoneInput from '../components/CountryPhoneInput';
import './Settings.css';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('profile');
  
  // Проверяем параметр section из URL
  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'security') {
      setActiveSection('security');
    }
    // Очищаем сообщение при изменении параметров URL
    setMessage({ type: '', text: '' });
  }, [searchParams]);
  
  // Профиль
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    country: '',
    avatar: null,
  });
  
  // Безопасность
  const [securityData, setSecurityData] = useState({
    email: '',
    phone: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  
  // Подтверждение телефона
  const [phoneVerification, setPhoneVerification] = useState({
    code: ['', '', '', ''],
    sending: false,
    verifying: false,
    error: '',
    codeSent: false,
    editing: false,
  });
  const phoneCodeInputRefs = useRef([]);
  
  // Нотификации
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    lesson_reminders: true,
    new_messages: true,
    system_updates: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const avatarInputRef = useRef(null);

  const buildProfileFromUser = (u) => ({
    first_name: u?.first_name || '',
    last_name: u?.last_name || '',
    bio: u?.bio || '',
    country: u?.country || '',
    avatar: u?.avatar_url || null,
  });

  const buildSecurityFromUser = (u) => ({
    email: u?.email || '',
    phone: u?.phone || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    if (user) {
      const profile = buildProfileFromUser(user);
      const security = buildSecurityFromUser(user);
      setProfileData(profile);
      setSecurityData(security);
    }
  }, [user]);

  // Очищаем сообщение при смене секции
  useEffect(() => {
    setMessage({ type: '', text: '' });
  }, [activeSection]);

  // Очищаем сообщение при монтировании компонента (загрузке страницы)
  useEffect(() => {
    setMessage({ type: '', text: '' });
  }, []);
  
  // Убрали автоматическую отправку кода - теперь только по кнопке

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleSecurityChange = (e) => {
    const { name, value } = e.target;
    setSecurityData({ ...securityData, [name]: value });
    if (name === 'phone') {
      // При изменении телефона сбрасываем статус подтверждения, если он был подтвержден
      if (user?.is_phone_verified && !phoneVerification.editing) {
        // Не сбрасываем, если не в режиме редактирования
        return;
      }
      setPhoneVerification({ 
        code: ['', '', '', ''], 
        sending: false, 
        verifying: false, 
        error: '',
        codeSent: false,
        editing: phoneVerification.editing
      });
    }
  };
  
  const handlePhoneCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }
    const newCode = [...phoneVerification.code];
    newCode[index] = value;
    setPhoneVerification({ ...phoneVerification, code: newCode, error: '' });
    
    if (value && index < 3) {
      phoneCodeInputRefs.current[index + 1]?.focus();
    }
  };
  
  const handlePhoneCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !phoneVerification.code[index] && index > 0) {
      phoneCodeInputRefs.current[index - 1]?.focus();
    }
  };
  
  const handleSendPhoneCode = async () => {
    if (!securityData.phone || securityData.phone.trim().length < 5) {
      setPhoneVerification({
        ...phoneVerification,
        error: 'Введите корректный номер телефона'
      });
      return;
    }
    
    setPhoneVerification({ ...phoneVerification, sending: true, error: '' });
    try {
      const phoneNumber = securityData.phone.trim();
      console.log('Отправка кода на телефон:', phoneNumber);
      const response = await api.post('/auth/verify/phone/send/', { phone: phoneNumber });
      console.log('Ответ сервера:', response.data);
      setPhoneVerification({ 
        ...phoneVerification, 
        sending: false, 
        error: '',
        codeSent: true
      });
      phoneCodeInputRefs.current[0]?.focus();
    } catch (error) {
      console.error('Ошибка отправки кода:', error);
      console.error('Детали ошибки:', error.response?.data);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Ошибка отправки кода';
      setPhoneVerification({
        ...phoneVerification,
        sending: false,
        error: errorMessage,
        codeSent: false
      });
    }
  };
  
  const handleVerifyPhoneCode = async () => {
    const codeString = phoneVerification.code.join('');
    if (codeString.length !== 4) {
      setPhoneVerification({ ...phoneVerification, error: 'Введите 4-значный код' });
      return;
    }
    
    setPhoneVerification({ ...phoneVerification, verifying: true, error: '' });
    try {
      const response = await api.post('/auth/verify/phone/verify/', { code: codeString });
      if (response.data.message) {
        await updateUser();
        setPhoneVerification({ 
          code: ['', '', '', ''], 
          sending: false, 
          verifying: false, 
          error: '',
          codeSent: false,
          editing: false
        });
        setMessage({ type: 'success', text: 'Телефон успешно подтвержден' });
      }
    } catch (error) {
      setPhoneVerification({
        ...phoneVerification,
        verifying: false,
        error: error.response?.data?.error || 'Неверный код'
      });
    }
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotifications({ ...notifications, [name]: checked });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileData({ ...profileData, avatar: file });
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('first_name', profileData.first_name);
      formData.append('last_name', profileData.last_name);
      formData.append('bio', profileData.bio);
      formData.append('country', profileData.country);
      if (profileData.avatar instanceof File) {
        formData.append('avatar', profileData.avatar);
      }

      await api.patch('/auth/me/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage({ type: 'success', text: 'Профиль успешно обновлен' });
      if (updateUser) {
        await updateUser();
      }
    } catch (error) {
      console.error('Ошибка обновления профиля:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Ошибка обновления профиля',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const data = {
        email: securityData.email,
        phone: securityData.phone,
      };

      if (securityData.new_password) {
        if (securityData.new_password !== securityData.confirm_password) {
          setMessage({ type: 'error', text: 'Новые пароли не совпадают' });
          setLoading(false);
          return;
        }
        data.new_password = securityData.new_password;
        data.current_password = securityData.current_password;
      }

      const response = await api.patch('/auth/me/', data);
      setMessage({ type: 'success', text: 'Настройки безопасности обновлены' });
      if (updateUser) {
        await updateUser();
      }
      setSecurityData({
        ...securityData,
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      console.error('Ошибка обновления безопасности:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Ошибка обновления настроек безопасности',
      });
    } finally {
      setLoading(false);
    }
  };

  const avatarPreviewUrl = useMemo(() => {
    if (!profileData.avatar) return null;
    if (typeof profileData.avatar === 'string') return profileData.avatar;
    return URL.createObjectURL(profileData.avatar);
  }, [profileData.avatar]);

  useEffect(() => {
    if (profileData.avatar instanceof File && avatarPreviewUrl) {
      return () => URL.revokeObjectURL(avatarPreviewUrl);
    }
  }, [profileData.avatar, avatarPreviewUrl]);

  const handleNotificationsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Здесь будет API для сохранения настроек нотификаций
      // await api.patch('/auth/notifications/', notifications);
      setMessage({ type: 'success', text: 'Настройки уведомлений сохранены' });
    } catch (error) {
      console.error('Ошибка сохранения уведомлений:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Ошибка сохранения настроек уведомлений',
      });
    } finally {
      setLoading(false);
    }
  };

  const countries = [
    { value: '', label: 'Выберите страну' },
    { value: 'ru', label: 'Россия' },
    { value: 'kz', label: 'Казахстан' },
    { value: 'by', label: 'Беларусь' },
    { value: 'ua', label: 'Украина' },
    { value: 'us', label: 'США' },
    { value: 'gb', label: 'Великобритания' },
    { value: 'de', label: 'Германия' },
    { value: 'fr', label: 'Франция' },
  ];

  const renderSaveButton = (formId) => (
    <div className="settings-card-actions">
      <button
        type="submit"
        form={formId}
        disabled={loading}
        className="btn-settings-save"
      >
        {loading ? 'Сохранение...' : 'Сохранить'}
      </button>
    </div>
  );

  return (
    <div className="settings-page">
      <Header pageTitle="Мой профиль" />
      <div className="settings-content">
        <div className="settings-container">
          <h1 className="settings-page-title">Мой профиль</h1>

          <nav className="settings-tabs" aria-label="Разделы профиля">
            <button
              type="button"
              className={`settings-tab ${activeSection === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              Профиль
            </button>
            <button
              type="button"
              className={`settings-tab ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => setActiveSection('security')}
            >
              Безопасность
            </button>
            <button
              type="button"
              className={`settings-tab ${activeSection === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveSection('notifications')}
            >
              Настройки и уведомления
            </button>
          </nav>

          <div className="settings-tab-panel">
              {activeSection === 'profile' && (
                <form
                  id="profile-form"
                  onSubmit={handleProfileSubmit}
                  className="settings-card"
                >
                  <div className="settings-card-header">
                    <h2 className="settings-card-title">Основная информация</h2>
                    {renderSaveButton('profile-form')}
                  </div>
                  <div className="settings-card-body profile-card-body">
                    <div className="profile-form-layout">
                      <div className="profile-fields">
                        <div className="profile-field-row">
                          <label htmlFor="first_name" className="profile-field-label">Имя</label>
                          <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            className="profile-field-input"
                            value={profileData.first_name}
                            onChange={handleProfileChange}
                          />
                        </div>
                        <div className="profile-field-row">
                          <label htmlFor="last_name" className="profile-field-label">Фамилия</label>
                          <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            className="profile-field-input"
                            value={profileData.last_name}
                            onChange={handleProfileChange}
                          />
                        </div>
                        <div className="profile-field-row">
                          <label htmlFor="country" className="profile-field-label">Страна</label>
                          <select
                            id="country"
                            name="country"
                            value={profileData.country}
                            onChange={handleProfileChange}
                            className="profile-field-input profile-field-select"
                          >
                            {countries.map((country) => (
                              <option key={country.value} value={country.value}>
                                {country.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="profile-field-row profile-field-row--textarea">
                          <label htmlFor="bio" className="profile-field-label">О себе</label>
                          <textarea
                            id="bio"
                            name="bio"
                            className="profile-field-input profile-field-textarea"
                            value={profileData.bio}
                            onChange={handleProfileChange}
                            rows={3}
                            placeholder="Расскажите немного о себе"
                          />
                        </div>
                      </div>
                      <div className="profile-avatar-column">
                        <div className="profile-avatar-wrap">
                          {avatarPreviewUrl ? (
                            <img
                              src={avatarPreviewUrl}
                              alt=""
                              className="profile-avatar-img"
                            />
                          ) : (
                            <div className="profile-avatar-placeholder" aria-hidden="true">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          id="avatar"
                          name="avatar"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="profile-avatar-input"
                        />
                        <label htmlFor="avatar" className="profile-upload-btn">
                          {profileData.avatar ? 'Изменить фото' : 'Загрузить фото'}
                        </label>
                        <p className="profile-avatar-hint">
                          Фото поможет преподавателю найти вас, если имя совпадает с другим учеником
                        </p>
                      </div>
                    </div>
                    {message.text && activeSection === 'profile' && (
                      <div className={`message message-${message.type}`}>
                        {message.text}
                      </div>
                    )}
                  </div>
                </form>
              )}

              {activeSection === 'security' && (
                <form
                  id="security-form"
                  onSubmit={handleSecuritySubmit}
                  className="settings-card"
                >
                  <div className="settings-card-header">
                    <h2 className="settings-card-title">Безопасность</h2>
                    {renderSaveButton('security-form')}
                  </div>
                  <div className="settings-card-body">
                <div className="settings-form settings-form--in-card">
                  
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={securityData.email}
                      onChange={handleSecurityChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Номер телефона</label>
                    <div className="phone-input-wrapper">
                      <CountryPhoneInput
                        value={securityData.phone}
                        onChange={(value) => handleSecurityChange({ target: { name: 'phone', value } })}
                        disabled={user?.is_phone_verified && securityData.phone && !phoneVerification.editing}
                        placeholder="Номер телефона"
                      />
                      {user?.is_phone_verified && securityData.phone && !phoneVerification.editing && (
                        <span className="verified-badge">✓ Подтвержден</span>
                      )}
                      {user?.is_phone_verified && securityData.phone && !phoneVerification.editing && (
                        <button
                          type="button"
                          onClick={() => {
                            setPhoneVerification({ ...phoneVerification, editing: true, codeSent: false, code: ['', '', '', ''] });
                            // Сбрасываем статус подтверждения при редактировании
                            handleSecurityChange({ target: { name: 'phone', value: securityData.phone } });
                          }}
                          className="btn-edit-phone"
                        >
                          Изменить
                        </button>
                      )}
                    </div>
                    {securityData.phone && securityData.phone.length > 5 && (!user?.is_phone_verified || phoneVerification.editing) && (
                      <div className="phone-verification">
                        {!phoneVerification.codeSent ? (
                          <div className="phone-verification-send">
                            <p className="verification-hint">Нажмите кнопку "Отправить код" для получения кода подтверждения на телефон</p>
                            <button
                              type="button"
                              onClick={handleSendPhoneCode}
                              disabled={phoneVerification.sending}
                              className="btn-send-phone-code"
                            >
                              {phoneVerification.sending ? 'Отправка...' : 'Отправить код'}
                            </button>
                          </div>
                        ) : (
                          <>
                            <p className="verification-hint">Введите код подтверждения, отправленный на телефон</p>
                            <div className="phone-code-inputs">
                              {phoneVerification.code.map((digit, index) => (
                                <input
                                  key={index}
                                  ref={(el) => (phoneCodeInputRefs.current[index] = el)}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength="1"
                                  value={digit}
                                  onChange={(e) => handlePhoneCodeChange(index, e.target.value)}
                                  onKeyDown={(e) => handlePhoneCodeKeyDown(index, e)}
                                  className="phone-code-input"
                                  autoComplete="off"
                                />
                              ))}
                            </div>
                            {phoneVerification.error && (
                              <div className="phone-verification-error">{phoneVerification.error}</div>
                            )}
                            <div className="phone-verification-actions">
                              <button
                                type="button"
                                onClick={handleVerifyPhoneCode}
                                disabled={phoneVerification.verifying || phoneVerification.code.join('').length !== 4}
                                className="btn-verify-phone"
                              >
                                {phoneVerification.verifying ? 'Проверка...' : 'Подтвердить'}
                              </button>
                              <button
                                type="button"
                                onClick={handleSendPhoneCode}
                                disabled={phoneVerification.sending}
                                className="btn-resend-phone"
                              >
                                {phoneVerification.sending ? 'Отправка...' : 'Отправить повторно'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-divider"></div>
                  <h4 className="subsection-title">Смена пароля</h4>

                  <div className="form-group">
                    <label htmlFor="current_password">Текущий пароль</label>
                    <input
                      type="password"
                      id="current_password"
                      name="current_password"
                      value={securityData.current_password}
                      onChange={handleSecurityChange}
                      placeholder="Оставьте пустым, если не хотите менять пароль"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="new_password">Новый пароль</label>
                    <input
                      type="password"
                      id="new_password"
                      name="new_password"
                      value={securityData.new_password}
                      onChange={handleSecurityChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirm_password">Подтвердите новый пароль</label>
                    <input
                      type="password"
                      id="confirm_password"
                      name="confirm_password"
                      value={securityData.confirm_password}
                      onChange={handleSecurityChange}
                    />
                  </div>

                  {message.text && activeSection === 'security' && (
                    <div className={`message message-${message.type}`}>
                      {message.text}
                    </div>
                  )}
                </div>
                  </div>
                </form>
              )}

              {activeSection === 'notifications' && (
                <form
                  id="notifications-form"
                  onSubmit={handleNotificationsSubmit}
                  className="settings-card"
                >
                  <div className="settings-card-header">
                    <h2 className="settings-card-title">Уведомления</h2>
                    {renderSaveButton('notifications-form')}
                  </div>
                  <div className="settings-card-body">
                <div className="settings-form settings-form--in-card">
                  
                  <div className="notification-group">
                    <div className="notification-item">
                      <div className="notification-info">
                        <label htmlFor="email_notifications" className="notification-label">
                          Email уведомления
                        </label>
                        <p className="notification-description">
                          Получать уведомления на email
                        </p>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          id="email_notifications"
                          name="email_notifications"
                          checked={notifications.email_notifications}
                          onChange={handleNotificationChange}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="notification-item">
                      <div className="notification-info">
                        <label htmlFor="lesson_reminders" className="notification-label">
                          Напоминания об уроках
                        </label>
                        <p className="notification-description">
                          Уведомления о предстоящих уроках
                        </p>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          id="lesson_reminders"
                          name="lesson_reminders"
                          checked={notifications.lesson_reminders}
                          onChange={handleNotificationChange}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="notification-item">
                      <div className="notification-info">
                        <label htmlFor="new_messages" className="notification-label">
                          Новые сообщения
                        </label>
                        <p className="notification-description">
                          Уведомления о новых сообщениях в чате
                        </p>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          id="new_messages"
                          name="new_messages"
                          checked={notifications.new_messages}
                          onChange={handleNotificationChange}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="notification-item">
                      <div className="notification-info">
                        <label htmlFor="system_updates" className="notification-label">
                          Системные обновления
                        </label>
                        <p className="notification-description">
                          Уведомления об обновлениях системы
                        </p>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          id="system_updates"
                          name="system_updates"
                          checked={notifications.system_updates}
                          onChange={handleNotificationChange}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                  {message.text && activeSection === 'notifications' && (
                    <div className={`message message-${message.type}`}>
                      {message.text}
                    </div>
                  )}
                </div>
                  </div>
                </form>
              )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Settings;
